import { 
  getAuth, 
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import rbacService from './rbacService';
import { normalizeOrganizationId } from '../utils/organizationAutoHeal';

const auth = getAuth();
const functions = getFunctions();

function withNormalizedOrganizationFields(userData, uid, email) {
  const orgId = normalizeOrganizationId(
    userData.organizationId ?? userData.organization_id
  );
  const profile = {
    uid,
    email,
    ...userData,
    ...(orgId ? { organizationId: orgId, organization_id: orgId } : {})
  };
  return profile;
}

async function healUserOrganizationIdOnProfile(uid, userData) {
  const raw = userData.organizationId ?? userData.organization_id;
  const orgId = normalizeOrganizationId(raw);
  if (!uid || !orgId || typeof raw !== 'object') return;
  try {
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'users', uid), {
      organizationId: orgId,
      organization_id: orgId,
      organizationIds: [orgId],
      primaryOrganizationId: orgId,
      updated_at: new Date().toISOString()
    });
    console.info('[authService] Repaired organizationId on user profile:', orgId);
  } catch (e) {
    console.warn('[authService] Could not repair organizationId on profile:', e?.message || e);
  }
}

// Authentication Service
const authService = {
  // Login user
  async login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Get user profile from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        throw new Error('User profile not found');
      }
      
      const userData = userDoc.data();
      
      // Check if user is active
      if (userData.status !== 'active') {
        await signOut(auth);
        throw new Error('User account is not active');
      }

      await healUserOrganizationIdOnProfile(user.uid, userData);
      
      return withNormalizedOrganizationFields(userData, user.uid, user.email);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Login with badge and PIN
  async loginWithBadge(badgeId, pin) {
    try {
      console.log('Attempting badge login for badge:', badgeId);
      
      // Call Cloud Function to authenticate badge + PIN
      // The Cloud Function will:
      // 1. Find user by badge_id
      // 2. Verify PIN hash
      // 3. Check account status
      // 4. Create custom auth token
      // 5. Return user data
      
      const loginWithBadgeFunction = httpsCallable(functions, 'loginWithBadge');
      const result = await loginWithBadgeFunction({ badgeId, pin });
      
      const { customToken, userData } = result.data;
      
      // Sign in with custom token
      const userCredential = await auth.signInWithCustomToken(customToken);
      const user = userCredential.user;
      
      return withNormalizedOrganizationFields(userData, user.uid, user.email);
    } catch (error) {
      console.error('Badge login error:', error);
      
      // Extract error message from Cloud Function error
      if (error.code === 'functions/not-found') {
        throw new Error('Badge not found in system');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error('Invalid PIN');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('Badge login not permitted for this account');
      } else if (error.code === 'functions/failed-precondition') {
        if (error.message && error.message.includes('PIN not configured')) {
          throw new Error('Badge PIN not configured. Contact your administrator.');
        }
        throw new Error('Account is locked or not active');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Badge login failed. Please try again.');
      }
    }
  },

  // Create new user via Cloud Function (admin only)
  async createUser(userData) {
    try {
      console.log('Creating user via Cloud Function:', userData);
      
      // Call the Cloud Function to create the user
      const createUserFunction = httpsCallable(functions, 'createUser');
      const result = await createUserFunction(userData);
      
      console.log('User created successfully:', result.data);
      return result.data;
    } catch (error) {
      console.error('User creation error:', error);
      
      // Extract error message from Cloud Function error
      if (error.code === 'functions/already-exists') {
        throw new Error('A user with this badge ID, employee ID, or email already exists');
      } else if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to create users');
      } else if (error.code === 'functions/invalid-argument') {
        throw new Error(error.message || 'Invalid user data provided');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to create user. Please try again.');
      }
    }
  },

  // Update user via Cloud Function
  async updateUser(userId, updates) {
    try {
      console.log('Updating user via Cloud Function:', { userId, updates });
      
      const updateUserFunction = httpsCallable(functions, 'updateUser');
      const result = await updateUserFunction({ userId, updates });
      
      console.log('User updated successfully:', result.data);
      return result.data;
    } catch (error) {
      console.error('User update error:', error);
      
      if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to update this user');
      } else if (error.code === 'functions/not-found') {
        throw new Error('User not found');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to update user. Please try again.');
      }
    }
  },

  // Delete user via Cloud Function
  async deleteUser(userId) {
    try {
      console.log('Deleting user via Cloud Function:', userId);
      
      const deleteUserFunction = httpsCallable(functions, 'deleteUser');
      const result = await deleteUserFunction({ userId });
      
      console.log('User deleted successfully:', result.data);
      return result.data;
    } catch (error) {
      console.error('User deletion error:', error);
      
      if (error.code === 'functions/permission-denied') {
        throw new Error('You do not have permission to delete users');
      } else if (error.code === 'functions/not-found') {
        throw new Error('User not found');
      } else if (error.message) {
        throw new Error(error.message);
      } else {
        throw new Error('Failed to delete user. Please try again.');
      }
    }
  },

  // Logout
  async logout() {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  },

  // Reset password
  async resetPassword(email) {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      throw error;
    }
  },

  // Send password reset via Cloud Function
  async sendPasswordResetLink(email) {
    try {
      console.log('Sending password reset link via Cloud Function:', email);
      
      const sendPasswordResetFunction = httpsCallable(functions, 'sendPasswordReset');
      const result = await sendPasswordResetFunction({ email });
      
      console.log('Password reset link generated:', result.data);
      return result.data;
    } catch (error) {
      console.error('Password reset error:', error);
      throw new Error(error.message || 'Failed to send password reset. Please try again.');
    }
  },

  // Setup password for new user via Cloud Function
  async setupPassword(token, newPassword) {
    try {
      console.log('Setting up password via token');

      const setupPasswordFunction = httpsCallable(functions, 'setupPassword');
      const result = await setupPasswordFunction({ token, newPassword });

      console.log('Password setup successful:', result.data);
      return result.data;
    } catch (error) {
      console.error('Password setup error:', error);
      throw new Error(error.message || 'Failed to set up password. Please try again.');
    }
  },

  // Change password
  async changePassword(newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('No user logged in');
      }
      
      await updatePassword(user, newPassword);
      
      // Update must_change_password flag
      await setDoc(doc(db, 'users', user.uid), {
        must_change_password: false,
        updated_at: new Date().toISOString()
      }, { merge: true });
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser() {
    return auth.currentUser;
  },

  // Get current user profile
  async getCurrentUserProfile() {
    try {
      const user = auth.currentUser;
      if (!user) return null;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return null;
      
      const userData = userDoc.data();
      await healUserOrganizationIdOnProfile(user.uid, userData);
      return withNormalizedOrganizationFields(userDoc.data(), user.uid, user.email);
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return onAuthStateChanged(auth, callback);
  },

  // Check if user has permission
  async checkPermission(permissionKey) {
    try {
      const user = auth.currentUser;
      if (!user) return false;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return false;
      
      const userData = userDoc.data();
      const role = await rbacService.getRoleById(userData.role_id);
      
      return role && role.permissions && role.permissions.includes(permissionKey);
    } catch (error) {
      console.error('Permission check error:', error);
      return false;
    }
  }
};

export default authService;

